import { Button, type ButtonProps, cn } from '@up2stay/up2promo-ui';
import { useDictionary } from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { motion, useReducedMotion } from 'motion/react';
import { type ElementType, useEffect, useRef } from 'react';
import { useMediaQuery } from 'usehooks-ts';

export default function ButtonWithEnter(
  props: ButtonProps & {
    text: string;
    isLoading?: boolean;
    href?: string;
    as?: ElementType;
  },
) {
  const translations = useDictionary();
  const lastClickedRef = useRef<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const reduceMotion = useReducedMotion();
  const { href, onClick, asChild } = props;
  const asProp = props.as;
  const isLink = !!(href || asProp || asChild);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      lastClickedRef.current = event.target as HTMLElement;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      const last = lastClickedRef.current;
      const isInsideFullViewportHeight =
        last && last.closest('.full-viewport-height');

      if (isInsideFullViewportHeight || !lastClickedRef.current) {
        if (isLink) {
          const wrapper = wrapperRef.current;
          if (wrapper) {
            const buttonElement = wrapper.querySelector(
              'button, a',
            ) as HTMLElement;
            if (buttonElement) {
              buttonElement.click();
            }
          }
        } else if (onClick) {
          const wrapper = wrapperRef.current;
          const buttonElement = wrapper?.querySelector('button') as HTMLElement;
          const syntheticEvent = {
            target: buttonElement || null,
            currentTarget: buttonElement || null,
            preventDefault: () => {},
            stopPropagation: () => {},
          } as unknown as React.MouseEvent<HTMLButtonElement>;
          onClick(syntheticEvent);
        }
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLink, onClick]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  const { text, isLoading, className, ...buttonProps } = props;

  return (
    <div
      ref={wrapperRef}
      className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2"
    >
      <motion.div
        className="setup-cta-press inline-flex w-full sm:w-auto"
        whileTap={reduceMotion ? undefined : { scale: 0.97 }}
        transition={
          reduceMotion
            ? undefined
            : { type: 'spring', stiffness: 520, damping: 28, mass: 0.6 }
        }
      >
        <Button
          variant="primary"
          size="lg"
          isLoading={isLoading}
          className={cn('min-h-11 w-full sm:w-auto', className)}
          {...buttonProps}
        >
          {asChild ? props.children : text}
        </Button>
      </motion.div>
      {!isMobile && (
        <p className="text-xs text-foreground/60">
          {translations.general.buttonWithEnter.press}{' '}
          <span className="font-semibold">
            {translations.general.buttonWithEnter.enter}
          </span>{' '}
          ↵
        </p>
      )}
    </div>
  );
}
